import { Component, Input, OnDestroy, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { DesktopStateService } from 'src/app/services/desktop-state.service';
import { FileWatchService, FileChangeEvent } from 'src/app/services/file-watch.service';
import { ConfigService } from 'src/app/services/config.service';
import { WidgetContextService } from 'src/app/services/widget-context.service';
import { DialogService } from '../../../services/dialog.service';

@Component({
    selector: 'app-monaco-widget',
    templateUrl: './monaco-widget.component.html',
    styleUrls: ['../base-widget/base-widget.component.scss', './monaco-widget.component.scss'],
    standalone: false
})
export class MonacoWidgetComponent extends BaseWidgetComponent implements OnInit, OnDestroy {
  private dialogs = inject(DialogService);
  @Input() path: any = null;
  @Input() title: string = null;
  /** The linked computer the file lives on ('' = the home); set from where the read found it. */
  @Input() host: string = '';

  content: string = null;
  originalContent: string = null; // To track changes
  hasUnsavedChanges: boolean = false;
  pathCopied: boolean = false;

  // External change notification
  showExternalChangeNotice: boolean = false;

  get filename(): string {
    if (!this.path) return null;
    return this.path.split('/').pop();
  }

  public editorOptions: any = {
    theme: 'vs-dark',
    language: 'plaintext', // Default language
    automaticLayout: true,
    wordWrap: true,
    scrollBeyondLastLine: false,
    minimap: {
      enabled: false,
    },
  };

  private editorInstance: any = null;
  private fileWatchSubscription: Subscription | null = null;

  constructor(
    private http: HttpClient,
    private desktopState: DesktopStateService,
    private fileWatch: FileWatchService,
    private cdr: ChangeDetectorRef,
    private _widgetContext: WidgetContextService,
  ) {
    super();
  }

  ngOnInit(): void {
    // Detect language based on file extension
    this.editorOptions.language = this.detectLanguage(this.path);

    // Register with desktop state service
    this.desktopState.registerEditor(this.id, 'MonacoWidget', this.path);

    // Load the content from the file via mux commander (per-user SSH)
    this.loadFile();

    // Subscribe to file changes
    this.fileWatch.watchFile(this.path);
    this.fileWatchSubscription = this.fileWatch.getChanges(this.path)
      .subscribe((event: FileChangeEvent) => {
        this.handleExternalChange(event);
      });

    // Add keyboard listener for Ctrl+S
    window.addEventListener('keydown', this.handleKeydown.bind(this));

    // Register widget context for Claude
    this._updateWidgetContext();
  }

  private _updateWidgetContext(): void {
    this._widgetContext.updateContext({
      widgetId: this.id,
      widgetType: 'MonacoWidget',
      path: this.path,
      state: {
        language: this.editorOptions?.language || 'plaintext',
        dirty: this.hasUnsavedChanges,
      },
    });
  }

  /**
   * Called when Monaco editor is initialized
   */
  onEditorInit(editor: any): void {
    this.editorInstance = editor;

    // Track cursor position changes
    editor.onDidChangeCursorPosition((e: any) => {
      this.desktopState.updateCursor(this.id, e.position.lineNumber, e.position.column);
    });

    // Track focus changes
    editor.onDidFocusEditorWidget(() => {
      this.desktopState.setActiveEditor(this.id);
      this._widgetContext.setFocused(this.id);
    });
  }

  ngOnDestroy(): void {
    // The widget is going away either way (a destroy cannot be refused):
    // offer to save what would be lost. The save needs only path + content.
    if (this.hasUnsavedChanges) {
      this.confirmSaveChanges();
    }

    // Unregister from desktop state and widget context
    this.desktopState.unregisterEditor(this.id);
    this._widgetContext.removeContext(this.id);

    // Stop watching file
    this.fileWatch.unwatchFile(this.path);
    this.fileWatchSubscription?.unsubscribe();

    window.removeEventListener('keydown', this.handleKeydown.bind(this));
    super.ngOnDestroy();
  }

  handleKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault();
      this.saveContent();
    }
  }

  async confirmSaveChanges(): Promise<boolean> {
    const userConfirmed = await this.dialogs.confirm(
      `You have unsaved changes in ${this.path}. Save them?`,
      { confirmText: 'Save', cancelText: 'Discard' });
    if (userConfirmed) {
      this.saveContent();
    }
    return userConfirmed;
  }

  saveContent(): void {
    if (this.content === this.originalContent) {
      console.log('No changes to save.');
      return;
    }

    // Use mux commander write_file (per-user SSH)
    this.http.post<any>(`${ConfigService.muxUrl}/commander/write_file`, {
      path: this.path,
      content: this.content,
      ...(this.host ? { host: this.host } : {})
    }).subscribe({
      next: (response) => {
        if (response.success) {
          console.log(`Saved content to ${this.path}`);
          this.originalContent = this.content;
          this.hasUnsavedChanges = false;
        } else {
          console.error('Save failed:', response.error);
        }
      },
      error: (err) => {
        console.error('Save failed:', err);
      }
    });
  }

  private loadFile(): void {
    const isUploadUrl = this.path?.startsWith('/mux/') || this.path?.startsWith('http');

    if (isUploadUrl) {
      this.http.get(this.path, { responseType: 'text' }).subscribe({
        next: (data) => {
          this.content = data;
          this.originalContent = data;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Failed to load upload:', err);
          this.content = '';
          this.originalContent = '';
          this.cdr.detectChanges();
        }
      });
      return;
    }

    this.http.post<any>(`${ConfigService.muxUrl}/commander/read_file`, { path: this.path, ...(this.host ? { host: this.host } : {}) })
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.host = response.metadata?.host ?? this.host;   // where it was found: saves go back there
            this.content = response.data;
            this.originalContent = response.data;
          } else {
            console.error('Failed to load file:', response.error);
            this.content = '';
            this.originalContent = '';
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Failed to load file:', err);
          this.content = '';
          this.originalContent = '';
          this.cdr.detectChanges();
        }
      });
  }

  copyPath(): void {
    if (!this.path) return;
    navigator.clipboard.writeText(this.path).then(() => {
      this.pathCopied = true;
      this.cdr.detectChanges();
      setTimeout(() => {
        this.pathCopied = false;
        this.cdr.detectChanges();
      }, 2000);
    });
  }

  onContentChange(): void {
    this.hasUnsavedChanges = this.content !== this.originalContent;
    this.desktopState.updateDirtyState(this.id, this.hasUnsavedChanges);
  }

  /**
   * Handle external file change notification
   */
  private handleExternalChange(event: FileChangeEvent): void {
    console.log('[MonacoWidget] External file change detected:', event);

    if (this.hasUnsavedChanges) {
      // Show notification to user - they have unsaved changes
      this.showExternalChangeNotice = true;
      this.cdr.detectChanges();
    } else {
      // No unsaved changes, reload automatically
      this.reloadFile();
    }
  }

  /**
   * Reload file content from disk
   */
  reloadFile(): void {
    this.http.post<any>(`${ConfigService.muxUrl}/commander/read_file`, { path: this.path, ...(this.host ? { host: this.host } : {}) })
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.host = response.metadata?.host ?? this.host;   // where it was found: saves go back there
            this.content = response.data;
            this.originalContent = response.data;
            this.hasUnsavedChanges = false;
            this.showExternalChangeNotice = false;
            this.desktopState.updateDirtyState(this.id, false);
            this.cdr.detectChanges();
            console.log('[MonacoWidget] File reloaded:', this.path);
          }
        },
        error: (err) => {
          console.error('[MonacoWidget] Failed to reload file:', err);
        }
      });
  }

  /**
   * Dismiss the external change notice (keep local changes)
   */
  dismissExternalChangeNotice(): void {
    this.showExternalChangeNotice = false;
    this.cdr.detectChanges();
  }

  private detectLanguage(path: string): string {
    const extension = path.split('.').pop()?.toLowerCase();

    const languageMap: { [key: string]: string } = {
      js: 'javascript',
      ts: 'typescript',
      py: 'python',
      java: 'java',
      html: 'html',
      css: 'css',
      scss: 'scss',
      json: 'json',
      md: 'markdown',
      xml: 'xml',
      yml: 'yaml',
      yaml: 'yaml',
      cpp: 'cpp',
      c: 'c',
      cs: 'csharp',
      php: 'php',
      rb: 'ruby',
      go: 'go',
      rs: 'rust',
      sh: 'shell',
      txt: 'plaintext',
    };

    return languageMap[extension] || 'plaintext'; // Default to plaintext if no match
  }
}
